import { message } from 'antd';
import { isEmpty } from 'lodash-es';
import { useTranslation } from 'next-i18next';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { useEffect, useMemo, useRef, useState } from 'react';

import useRegisterOrLoginGuide from '@/lib/hooks/common/useRegisterOrLoginGuide';
import { fetchInsFamousQueryPostsData } from '@/lib/service/anonymously';
import { InstagramQueryPostsModel } from '@/types/anonymously.interface';
import { InstagramPostTemplateDataModel } from '@/types/home';
import CustomersTalkUsContent from '../LandingPage/CustomersTalkUsContent';
import { CommonBackContent } from '../Layouts/CommonBackContent';
import useCheckIsMobile from '@/lib/hooks/useCheckIsMobile';
import InstagramAccountInfo from '../Common/InstagramAccountInfo';
import useMediaNameSync from '@/lib/hooks/useMediaNameSync';

const Disclaimer = dynamic(() => import('../LandingPage/Disclaimer'), { ssr: true });
const FAQContent = dynamic(() => import('../LandingPage/FAQContent'), { ssr: true });
const DownloadInstagramContent = dynamic(() => import('../LandingPage/DownloadInstagramContent'), {
  ssr: true,
});
const TopSearchContent = dynamic(() => import('../LandingPage/TopSearch'), { ssr: true });
const ChooseContent = dynamic(() => import('../LandingPage/ChooseContent'), { ssr: true });
const GeneralExampDownloadContent = dynamic(() => import('../LandingPage/GeneralExampDownloadContent'), { ssr: true });
const WhyDownloadStoriesContent = dynamic(() => import('../LandingPage/WhyDownloadStoriesContent'), { ssr: true });
const InstagramActivityTrackerContent = dynamic(() => import('../LandingPage/InstagramActivityTracker'), { ssr: true });
const BestTipsContent = dynamic(() => import('../LandingPage/BestTipsContent'), { ssr: true });
const DownloadStoriesBottomContent = dynamic(() => import('../LandingPage/DownloadStoriesBottomContent'), {
  ssr: true,
});
const PostListContent = dynamic(() => import('./PostListContent'), { ssr: false });
const CommonSimpleModal = dynamic(() => import('../Common/CommonSimpleModal'), { ssr: false });
const PostInfoPannel = dynamic(() => import('./PostInfoPannel'), { ssr: false });

const WebViewerForInstagramSub = ({ data }: { data?: any }) => {
  const [contentData, setContentData] = useState<InstagramPostTemplateDataModel>(data?.contentData);
  const resultRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();
  const { isMobile } = useCheckIsMobile();
  const { isLogin, guideToRegisterOrLogin } = useRegisterOrLoginGuide();
  const [searchStr, setSearchStr, writeToStorage] = useMediaNameSync('');

  const [loading, setLoading] = useState(false);
  const [inputVisible, setInputVisible] = useState(true);
  const [currentSelectedPostCode, setCurrentSelectedPostCode] = useState<string>();
  const [postDrawerOpen, setPostDrawerOpen] = useState(false);
  const searchRequestRef = useRef<{ mediaName: string | null; isRequesting: boolean }>({
    mediaName: null,
    isRequesting: false,
  });

  const [queryPostsModel, setQueryPostsModel] = useState<InstagramQueryPostsModel>(null);
  const [alertInfo, setAlertInfo] = useState<any>(null);
  const router = useRouter();
  const { media_name } = router.query;

  const currentSelectedPost = useMemo(() => {
    if (queryPostsModel?.items?.length > 0) {
      return queryPostsModel?.items?.find((item) => item?.code === currentSelectedPostCode);
    }
    return null;
  }, [queryPostsModel, currentSelectedPostCode]);

  useEffect(() => {
    setContentData(data?.contentData);
  }, [data]);

  useEffect(() => {
    if (!media_name || media_name === searchRequestRef.current.mediaName) {
      return;
    }

    // INFO: 暂时不限制未登录用户搜索
    // if (!isLogin) {
    //   return;
    // }

    if (searchRequestRef.current.isRequesting) {
      return;
    }

    setSearchStr(media_name as string);
    searchRequestRef.current.mediaName = media_name as string;
    onSearch(media_name as string);
  }, [media_name]);

  const onValueChanged = (e?: string) => {
    setSearchStr(e || '');
  };

  const srollToResultView = () => {
    let element = resultRef.current;
    if (element === undefined) {
      return;
    }
    const yOffset = -50;
    const y = element.getBoundingClientRect().top + window.scrollY + yOffset;
    window.scrollTo({ top: y, behavior: 'smooth' });
  };

  const onSearch = (e?: string, pageToken?: string) => {
    // INFO: 暂时不限制未登录用户搜索
    // if (!isLogin) {
    //   onOpenLoginModal(true);
    //   onChangedLoginView(false);
    //   return;
    // }

    if (e?.length > 0) {
      // 手动写入搜索内容到 SessionStorage
      writeToStorage(e);

      if (loading) {
        return;
      }
      setLoading(true);
      searchRequestRef.current.isRequesting = true;

      const newQuery = { ...router.query, media_name: e };
      router.push(
        {
          pathname: router.pathname,
          query: newQuery,
        },
        undefined,
        { shallow: true }
      );

      if (e !== searchStr) {
        setQueryPostsModel(null);
        setSearchStr(e);
      }

      fetchInsFamousQueryPostsData(e, pageToken, { bizFuncName: 'post_likes', isPremium: false })
        .then((res) => {
          if (res?.code === 0) {
            if (isEmpty(pageToken)) {
              res?.data?.items[0]?.code && setCurrentSelectedPostCode(res?.data?.items[0]?.code);
              setQueryPostsModel(res?.data ?? {});
              srollToResultView();
            } else {
              const tempList = [...queryPostsModel?.items, ...res?.data?.items];
              setQueryPostsModel({ ...res?.data, items: tempList });
            }
            return;
          } else {
            setQueryPostsModel(null);
          }

          if (res?.code === 22002) {
            setAlertInfo({
              title: t('Instagram Account Not Found'),
              desc: t(
                "Oops! We couldn't find an Instagram account matching the username or profile link you entered. Please double-check your input and try again."
              ),
              btnTitle: t('Try Again'),
            });
          } else if (res?.code === 22004) {
            setAlertInfo({
              title: t('Private Account Detected'),
              desc: t(
                'The Instagram account you entered is private. DolphinRadar only provides stories and highlights for public accounts. Please enter a public account to continue.'
              ),
              btnTitle: t('Try Another Account'),
            });
          } else if (res?.code === 22005) {
            setQueryPostsModel({});
          } else if (res?.code === 10001) {
            setAlertInfo({
              title: t('Invalid Instagram Username'),
              desc: t('The username you entered is not valid. Please check the username and try again.'),
              btnTitle: t('TRYAGAIN'),
            });
          } else {
            message.error(res?.message);
          }
        })
        .catch((error) => {})
        .finally(() => {
          setLoading(false);
          searchRequestRef.current.isRequesting = false;
        });
    } else {
    }
  };

  return (
    <>
      <CommonBackContent style={{ minHeight: '100vh' }}>
        <TopSearchContent
          inputPlaceholder={t('Enter a username')}
          topInfo={contentData?.contentInfo?.hero}
          loading={loading}
          searchVal={searchStr}
          showSearchBtnWithFullWidth
          onValueChanged={onValueChanged}
          onSearchClick={onSearch}
          onInputVisibleChanged={(visible) => setInputVisible(visible)}
          // INFO: 由于有 toolkits 导航栏，所以暂时不需要选项卡
          // searchTabs={[
          //   {
          //     label: (
          //       <span className="flex items-center gap-2">
          //         <span className="text-[20px]">
          //           <svg
          //             width="20"
          //             height="20"
          //             viewBox="0 0 20 20"
          //             fill="#fff"
          //             xmlns="http://www.w3.org/2000/svg"
          //           >
          //             <g>
          //               <path
          //                 id="Vector"
          //                 d="M13.8359 13.1641L17.1693 9.83073C18.6693 8.33073 18.6693 5.83073 17.1693 4.2474L17.0859 4.16406C15.5859 2.66406 13.0859 2.66406 11.5026 4.16406L10.0026 5.66406L8.5026 4.16406C7.0026 2.58073 4.5026 2.58073 2.91927 4.16406L2.83594 4.2474C1.2526 5.83073 1.2526 8.33073 2.83594 9.83073L7.2526 14.2474C7.33594 14.3307 7.41927 14.4141 7.58594 14.4974L10.0859 16.9974L13.8359 13.1641Z"
          //                 stroke="currentColor"
          //                 strokeWidth="2"
          //               />
          //             </g>
          //           </svg>
          //         </span>
          //         <span>{t('Post Likes')}</span>
          //       </span>
          //     ),
          //     value: 'post-likes',
          //   },
          //   {
          //     label: (
          //       <span className="flex items-center gap-2">
          //         <span className="text-[20px]">
          //           <svg
          //             width="20"
          //             height="20"
          //             viewBox="0 0 20 20"
          //             fill="#fff"
          //             xmlns="http://www.w3.org/2000/svg"
          //           >
          //             <g>
          //               <path
          //                 id="Vector"
          //                 d="M6.58724 17.8618C6.51774 17.8618 6.44784 17.847 6.38224 17.8168C6.20844 17.7368 6.09684 17.5629 6.09684 17.3713V15.2808C6.09684 15.0212 5.88564 14.8101 5.62564 14.8101H3.22564C2.42484 14.8101 1.77344 14.1586 1.77344 13.3583V4.60034C1.77344 3.79984 2.42484 3.14844 3.22564 3.14844H17.0695C17.8698 3.14844 18.5212 3.79984 18.5212 4.60034V13.3584C18.5212 14.1587 17.8698 14.8102 17.0695 14.8102H10.5031C10.3906 14.8102 10.2814 14.8502 10.1957 14.9242L6.90634 17.7437C6.81574 17.8215 6.70224 17.8618 6.58724 17.8618Z"
          //                 stroke="currentColor"
          //                 strokeWidth="2"
          //               />
          //             </g>
          //           </svg>
          //         </span>
          //         <span>{t('Post Comments')}</span>
          //       </span>
          //     ),
          //     value: 'post-comments',
          //   },
          // ]}
          // defaultSearchTab={contentData?.function_child}
          // onSearchTabClick={(value) => {
          //   router.push({
          //     pathname: `/instagram-viewer/${value}`,
          //     query: media_name ? { media_name } : {},
          //   });
          // }}
        />

        <div className="max-w-[984px] mx-auto">
          {/* 当前搜索的用户信息 */}
          {queryPostsModel && <InstagramAccountInfo mediaName={queryPostsModel?.post_user?.username} />}
          {/* 当前帖子标题 */}
          {queryPostsModel?.items?.length > 0 && (
            <div className="text-xl mt-8 mb-8 font-medium max-md:my-5 max-md:text-base">
              {currentSelectedPostCode && currentSelectedPostCode !== queryPostsModel?.items[0]?.code ? (
                <span>{t('Selected Post')}</span>
              ) : (
                <>
                  <span>{t('Latest posts from')}</span>
                  <span>{` @${queryPostsModel?.post_user?.username}`}</span>
                </>
              )}
            </div>
          )}
          {/* 帖子内容 & 评论/点赞信息 */}
          {currentSelectedPost && (
            <PostInfoPannel
              functionChild={contentData?.function_child}
              item={currentSelectedPost}
              drawerOpen={postDrawerOpen}
              onDrawerClose={() => setPostDrawerOpen(false)}
            />
          )}

          {/* 帖子列表标题 */}
          {queryPostsModel?.items?.length > 0 && (
            <div className="text-xl mt-12 mb-8 font-medium max-md:mt-9 max-md:mb-5 max-md:text-base">
              <span>{t('View all posts from')}</span>
              <span>{` @${queryPostsModel?.post_user?.username}`}</span>
            </div>
          )}
          {/* 帖子列表 */}
          <div ref={resultRef}>
            <PostListContent
              loading={loading}
              data={queryPostsModel}
              onNextClick={() => {
                guideToRegisterOrLogin(() => {
                  if (isEmpty(searchStr) || isEmpty(queryPostsModel?.page_token)) {
                    return;
                  }
                  onSearch(searchStr, queryPostsModel?.page_token);
                });
              }}
              onItemClick={(item) => {
                guideToRegisterOrLogin(() => {
                  // 设置当前选中的帖子
                  item.code && setCurrentSelectedPostCode(item?.code);
                  // 打开帖子详情弹层，移动端打开
                  isMobile && setPostDrawerOpen(true);
                });
              }}
            />
          </div>

          <Disclaimer />
        </div>
        <DownloadInstagramContent data={contentData?.contentInfo['section-1']} />
        <ChooseContent data={contentData?.contentInfo['section-2']} />
        <GeneralExampDownloadContent data={contentData?.contentInfo['section-3']} />
        <WhyDownloadStoriesContent data={contentData?.contentInfo['section-4']} />
        <InstagramActivityTrackerContent
          contentData={contentData}
          isTracker={true}
          webBg={'https://res-front.pumpsoul.com/ins-prod/image/activity_tracker_bg_web_0110_1.webp'}
          phoneBg={'https://res-front.pumpsoul.com/ins-prod/image/activity_tracker_bg_phone_0109.webp'}
        />
        <CustomersTalkUsContent contentInfo={contentData?.contentInfo?.['section-user-reviews']} />
        {contentData?.faqList && (
          <FAQContent faqInfo={contentData?.contentInfo?.['section-faq']} faqList={contentData?.faqList} />
        )}

        <BestTipsContent
          title={contentData?.contentInfo?.['section-top-blog']?.title}
          blogList={contentData?.topBlogList}
        />
        <BestTipsContent title={contentData?.contentInfo?.['section-blog']?.title} blogList={contentData?.blogList} />
      </CommonBackContent>

      <DownloadStoriesBottomContent
        bottomInfo={contentData?.contentInfo['section-buttom']}
        placeholder={t('Enter a username')}
        buttonTitle={t('Search Now')}
        loading={loading}
        searchVal={searchStr}
        inputVisible={inputVisible}
        onValueChanged={onValueChanged}
        onSearchClick={(e) => {
          onSearch(e);
        }}
      />

      <CommonSimpleModal
        open={alertInfo !== null}
        title={alertInfo?.title}
        desc={alertInfo?.desc}
        btnTitle={alertInfo?.btnTitle}
        closeAction={(isOk) => {
          if (isOk && alertInfo?.href) {
            router.push(alertInfo?.href);
          }
          setAlertInfo(null);
        }}
      />
    </>
  );
};

export default WebViewerForInstagramSub;
